// Premium through PayPal auto-renew, as the legacy web app: PayPal's button subscribes to the plan with
// custom_id = the account's email, PayPal bills the renewals and tells the integration API, which grants the
// subscription. GET /me/billing says where the account stands; cancelling goes through the API.
import { useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { premiumFeatures, useFeature, type PremiumFeature } from '@/auth/me';
import { config } from '@/config';

export interface Billing {
  premium: boolean;
  /** ISO date: the end of the period paid for. */
  premiumUntil?: string | null;
  autoRenew: boolean;
  planName?: string | null;
  payPalClientId: string;
  planId: string;
  planTitle: string;
  price: string;
  /** Every subscription period, latest first. */
  history?: BillingPeriod[];
}

export interface BillingPeriod {
  startDate: string;
  endDate: string;
  orderDate?: string;
  type?: string | null;
  reference?: string | null;
  active: boolean;
}

const billingKey = ['me', 'billing'];

export function useBilling(enabled = true) {
  return useQuery<Billing>({
    queryKey: billingKey,
    enabled: enabled && !config.authDisabled,
    queryFn: async () => {
      const response = await apiFetch('/me/billing');
      if (!response.ok) throw new Error(`GET /me/billing: ${response.status}`);
      return (await response.json()) as Billing;
    },
  });
}

export function useCancelAutoRenew() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await apiFetch('/me/billing/cancelAutoRenew', { method: 'POST' });
      if (!response.ok) throw new Error(`cancelAutoRenew: ${response.status}`);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
}

/**
 * After PayPal approves, the subscription arrives through PayPal's webhook a few seconds later: ask again until the
 * account shows auto-renew (or give up after a minute), then refresh /me so Premium features unlock.
 */
export async function waitForSubscription(
  refetch: () => Promise<{ data?: Billing }>,
  signal: AbortSignal,
  delayMs = 3000,
  attempts = 20,
): Promise<boolean> {
  for (let i = 0; i < attempts && !signal.aborted; i++) {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    const { data } = await refetch();
    if (data?.autoRenew || data?.premium) return true;
  }
  return false;
}

/** Why Subscribe is open: the top bar's button (no feature), or a Premium feature the user chose. */
export interface SubscribeRequest {
  feature?: PremiumFeature;
  /** Carries on with what the user was doing, once the subscription has reached the account. */
  then?: () => void;
}

let request: SubscribeRequest | null = null;
const listeners = new Set<() => void>();

/** Opens Subscribe (`true`, or for a feature), or closes it (`false`). */
export function openSubscribe(open: boolean | SubscribeRequest = true) {
  request = open === false ? null : open === true ? {} : open;
  listeners.forEach((listener) => listener());
}

function useRequest(): SubscribeRequest | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => request,
  );
}

export function isSubscribeOpen(): boolean {
  return request !== null;
}

export function getSubscribeRequest(): SubscribeRequest | null {
  return request;
}

export function useSubscribeOpen(): boolean {
  return useRequest() !== null;
}

export function useSubscribeRequest(): SubscribeRequest | null {
  return useRequest();
}

/** The name the API gave, if the app knows it. */
export function asPremiumFeature(name: string | undefined): PremiumFeature | undefined {
  return premiumFeatures.find((feature) => feature === name);
}

/**
 * A Premium feature: whether this account has it, and `require(action)`, which runs the action when it does and
 * otherwise offers Premium for the feature and runs the action once subscribed.
 */
export function usePremiumGate(feature: PremiumFeature) {
  const allowed = useFeature(feature);
  return {
    allowed,
    require(action?: () => void): boolean {
      if (allowed) {
        action?.();
        return true;
      }
      openSubscribe({ feature, then: action });
      return false;
    },
  };
}

interface PayPalButtons {
  render(container: HTMLElement): Promise<void>;
  close?(): Promise<void>;
}

interface PayPalNamespace {
  Buttons(options: {
    style?: Record<string, string>;
    createSubscription(
      data: unknown,
      actions: { subscription: { create(o: object): Promise<string> } },
    ): Promise<string>;
    onApprove(data: { subscriptionID?: string }): void;
    onError?(error: unknown): void;
  }): PayPalButtons;
}

declare global {
  interface Window {
    paypal?: PayPalNamespace;
  }
}

let sdk: Promise<PayPalNamespace> | undefined;

/** PayPal's JS SDK for subscriptions, loaded once when the Subscribe dialog first needs it. */
export function loadPayPal(clientId: string): Promise<PayPalNamespace> {
  sdk ??= new Promise<PayPalNamespace>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&vault=true&intent=subscription&currency=GBP`;
    script.dataset.sdkIntegrationSource = 'button-factory';
    script.onload = () => (window.paypal ? resolve(window.paypal) : reject(new Error('PayPal SDK missing')));
    script.onerror = () => {
      sdk = undefined;
      script.remove();
      reject(new Error('PayPal SDK failed to load'));
    };
    document.head.appendChild(script);
  });
  return sdk;
}
